import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Privacy = () => {
  const { t, i18n } = useTranslation();

  return (
    <>
      <Helmet>
        <html lang={i18n.language} />
        <title>{t('privacy.title')} | Tap'nGrab</title>
        <meta name="description" content="Privacy policy for Tap'nGrab smart coolers service in Montreal and South Shore, Quebec." />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`${t('privacy.title')} | Tap'nGrab`} />
        <meta property="og:description" content="Privacy policy for Tap'nGrab smart coolers service in Montreal and South Shore, Quebec." />
        <meta property="og:url" content={`https://tapngrab.ca/${i18n.language === 'fr' ? 'fr/' : ''}privacy`} />
        <meta property="og:image" content="https://tapngrab.ca/images/smart-store.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content={i18n.language === 'fr' ? 'fr_CA' : 'en_CA'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${t('privacy.title')} | Tap'nGrab`} />
        <meta name="twitter:description" content="Privacy policy for Tap'nGrab smart coolers service in Montreal and South Shore, Quebec." />
        <meta name="twitter:image" content="https://tapngrab.ca/images/smart-store.png" />
        <link rel="canonical" href="https://tapngrab.ca/privacy" />
        <link rel="alternate" hreflang="fr" href="https://tapngrab.ca/fr/privacy" />
        <link rel="alternate" hreflang="en" href="https://tapngrab.ca/privacy" />
      </Helmet>

      <div className="text-slate-800 bg-white">
        <Header />
        
        {/* Hero Section */}
        <section className="hero-grad py-16 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-slate-900 sm:text-5xl lg:text-6xl">
                {t('privacy.title')}
              </h1>
              <p className="mt-6 text-lg text-slate-600">
                {t('privacy.last_updated')}
              </p>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="bg-white py-16 sm:py-24">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <div className="space-y-8">
              <div className="bg-slate-50 rounded-2xl p-8">
                <p className="text-lg text-slate-700 leading-relaxed">
                  {t('privacy.introduction')}
                </p>
              </div>

              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">
                  {t('privacy.information_we_collect')}
                </h2>
                
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-slate-800">
                    {t('privacy.personal_information')}
                  </h3>
                  <p className="text-slate-700 leading-relaxed">
                    {t('privacy.personal_info_description')}
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-slate-700">
                    {t('privacy.personal_info_list', { returnObjects: true }).map((item, index) => (
                      <li key={index} className="leading-relaxed">{item}</li>
                    ))}
                  </ul>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-slate-800">
                    {t('privacy.automatically_collected')}
                  </h3>
                  <p className="text-slate-700 leading-relaxed">
                    {t('privacy.auto_collected_description')}
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-slate-700">
                    {t('privacy.auto_collected_list', { returnObjects: true }).map((item, index) => (
                      <li key={index} className="leading-relaxed">{item}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">
                  {t('privacy.how_we_use')}
                </h2>
                <p className="text-slate-700 leading-relaxed">
                  {t('privacy.use_description')}
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-700">
                  {t('privacy.use_list', { returnObjects: true }).map((item, index) => (
                    <li key={index} className="leading-relaxed">{item}</li>
                  ))}
                </ul>
              </div>

              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">
                  {t('privacy.information_sharing')}
                </h2>
                <p className="text-slate-700 leading-relaxed">
                  {t('privacy.sharing_description')}
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-700">
                  {t('privacy.sharing_list', { returnObjects: true }).map((item, index) => (
                    <li key={index} className="leading-relaxed">{item}</li>
                  ))}
                </ul>
              </div>

              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">
                  {t('privacy.data_security')}
                </h2>
                <p className="text-slate-700 leading-relaxed">
                  {t('privacy.security_description')}
                </p>
              </div>

              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">
                  {t('privacy.your_rights')}
                </h2>
                <p className="text-slate-700 leading-relaxed">
                  {t('privacy.rights_description')}
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-700">
                  {t('privacy.rights_list', { returnObjects: true }).map((item, index) => (
                    <li key={index} className="leading-relaxed">{item}</li>
                  ))}
                </ul>
              </div>

              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">
                  {t('privacy.cookies_tracking')}
                </h2>
                <p className="text-slate-700 leading-relaxed">
                  {t('privacy.cookies_description')}
                </p>
              </div>

              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">
                  {t('privacy.third_party')}
                </h2>
                <p className="text-slate-700 leading-relaxed">
                  {t('privacy.third_party_description')}
                </p>
              </div>

              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">
                  {t('privacy.children_privacy')}
                </h2>
                <p className="text-slate-700 leading-relaxed">
                  {t('privacy.children_description')}
                </p>
              </div>

              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">
                  {t('privacy.changes_policy')}
                </h2>
                <p className="text-slate-700 leading-relaxed">
                  {t('privacy.changes_description')}
                </p>
              </div>

              <div className="bg-slate-50 rounded-2xl p-8 space-y-6">
                <h2 className="text-2xl font-bold text-slate-900">
                  {t('privacy.contact_us')}
                </h2>
                <p className="text-slate-700 leading-relaxed">
                  {t('privacy.contact_description')}
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-700">
                  {t('privacy.contact_list', { returnObjects: true }).map((item, index) => (
                    <li key={index} className="leading-relaxed">{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default Privacy;
